import React, { useEffect } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { Box, Divider, Grid, IconButton, Typography } from '@mui/material';
import { AppButton } from '../../AppButton';
import KenColors from '../../../Helpers/Themes/kenColors';
import AddIcon from '@mui/icons-material/Add';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RemoveIcon from '@mui/icons-material/Remove';
import { EventFormJsonFields } from '../../../interfaces/Events';
import { useStyles } from '../styles';
import { enqueueSnackbar } from 'notistack';
import RowContent from '../RowContent';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

interface ArrayFieldRowProps {
  items: any;
  index: number;
  props: EventFormJsonFields;
  arrayFields: any[];
  handleRemoveRecord: (key: number) => void;
  append: (value: any) => void;
  classes: Record<string, string>;
  control: any;
  handleSubmit: any;
}

const ArrayFieldRow: React.FC<ArrayFieldRowProps> = ({
  items,
  index,
  props,
  arrayFields,
  handleRemoveRecord,
  append,
  classes,
  control,
  handleSubmit,
}) => {
  const isRulesField = props.metadata?.name === 'rules';
  const watchedStepValue = useWatch({
    name: `${props.metadata?.name}.${index}.Step`,
    control,
  });
  const canAddMore = !isRulesField || (watchedStepValue && watchedStepValue.trim().length > 0);

  const cardData = props.metadata?.fieldsToShow;
  switch (props.arrayVariant) {
    case 'variant1':
      return (
        <>
          {index === arrayFields.length - 1 ? (
            <Box
              width={'100%'}
              display={'flex'}
              component={'form'}
              onSubmit={handleSubmit((data:any) => {
                alert(data);
              })}
              flexDirection={props.metadata?.actionAlingment === 'bottom' ? 'column-reverse' : 'column'}
              marginTop={index !== 0 && props.metadata?.removeMultiple ? '20px' : '0px'}
              key={items.id}
            >
              <Typography className={classes.scheduleHeader}>{props.title}</Typography>
              <Grid item container md={12} lg={12} spacing={1}>
                {props.fieldsArray?.map((rows:any) => {
                  return (
                    <Grid item xs={rows.grid.xs} md={rows.grid.md} lg={rows.grid.lg}>
                      {rows.fields.map((fields:any) => {
                        fields['name'] = fields.dynamicName ? `${fields.dynamicName} ${index + 1}` : fields.name;
                        return <RowContent arrayIndex={index} fields={{ ...fields }} arrayName={`${props.metadata?.name}.${index}`} />;
                      })}
                    </Grid>
                  );
                })}
              </Grid>
              <Box component={'div'} className="d-flex justify-content-between">
                <AppButton
                  size="medium"
                  variant="contained"
                  fullWidth={false}
                  others={{ color: '#2245B5', borderRadius: '8px', padding: '12px 24px' }}
                  background="#BFCEFF"
                  endIcon={<AddIcon color={'#2245B5' as any} />}
                  type="submit"
                >
                  Save & another session
                </AppButton>
                <Box />
              </Box>
            </Box>
          ) : (
            <Box component={'div'} sx={{ background: '#F6F8FB', borderRadius: '12px', padding: '20px', margin: '10px 0px' }}>
              <div className="d-flex justify-content-between align-items-center">
                <Typography color={'#373A45'} fontSize={'16px'} fontWeight={'500'}>
                  Session {index + 1}
                </Typography>
                <div className="d-flex gap-3">
                  <IconButton>
                    <DeleteOutlineOutlinedIcon />
                  </IconButton>
                  <IconButton>
                    <EditOutlinedIcon />
                  </IconButton>
                </div>
              </div>
              <Divider sx={{ margin: '15px 0px' }} />
              <Box component={'div'} className="d-flex" gap={'40px'}>
                {cardData?.map((subItems: string, index: number) => {
                  const fieldArray = props?.fieldsArray || [];
                  let currentField;
                  for (let i = 0; i < fieldArray.length; i++) {
                    const fields = fieldArray[i].fields || [];
                    for (let i = 0; i < fields.length; i++) {
                      const field = fields[i];
                      if (field.name === subItems) {
                        currentField = field;
                      }
                    }
                  }
                  const fieldShowValue = items[subItems] || '-';
                  return (
                    items[subItems] && (
                      <Box component={'div'} className="d-flex flex-column" gap={'6px'} key={index}>
                        <Typography color={'#8C8E94'} fontSize={'14px'} fontWeight={'500'}>
                          {currentField?.fieldToShow?.label || ''}
                        </Typography>
                        <Typography color={'#373A45'} fontSize={'16px'} fontWeight={'500'}>
                          {fieldShowValue}
                        </Typography>
                      </Box>
                    )
                  );
                })}
              </Box>
            </Box>
          )}
        </>
      );
    default:
      return (
        <Box
          width={'100%'}
          display={'flex'}
          flexDirection={props.metadata?.actionAlingment === 'bottom' ? 'column-reverse' : 'column'}
          marginTop={index !== 0 && props.metadata?.removeMultiple ? '20px' : '0px'}
          key={items.id}
        >
          <div className={classes.scheduleHeaderContainer}>
            <div>
              {index === 0 ? (
                <>
                  {props?.title && <Typography style={{ fontSize: props.title === "Rules" ? "12px" : "16px", fontWeight: "550" }}>{props.title}</Typography>}
                  {props?.subTitle && <Typography style={{ fontSize: props.title === "Rules" ? "12px" : "14px", fontWeight: "550" }}>{props.subTitle}</Typography>}
                </>
              ) : props.metadata?.removeMultiple && index === arrayFields.length - 1 ? (
                <AppButton
                  width="100px"
                  background={KenColors.ClientPrimaryGrey}
                  variant="contained"
                  className={classes.arrayButton}
                  size="small"
                  startIcon={<RemoveIcon />}
                  onClick={() => {
                    handleRemoveRecord(index);
                  }}
                >
                  Remove
                </AppButton>
              ) : null}
              {props?.metadata?.removeNeeded && (
                <AppButton
                  variant="contained"
                  size="small"
                  others={{ margin: '10px 0px' }}
                  className={classes.arrayButton2}
                  endIcon={<CloseRoundedIcon />}
                  background={KenColors.ClientBlue4}
                  onClick={() => {
                    handleRemoveRecord(index);
                  }}
                >
                  Speaker {index + 1}
                </AppButton>
              )}
            </div>
            {props.metadata?.removeMultiple && (
              <>
                {index + 1 === arrayFields.length ? (
                  <AppButton
                    width="100px"
                    background={KenColors.ClientPrimaryGrey}
                    variant="contained"
                    size="small"
                    className={classes.arrayButton}
                    startIcon={<AddIcon />}
                    onClick={() => {
                      append(props.defaultFields);
                    }}
                    disabled={!canAddMore}
                  >
                    Add more
                  </AppButton>
                ) : (
                  <AppButton
                    width="100px"
                    background={KenColors.ClientPrimaryGrey}
                    variant="contained"
                    className={classes.arrayButton}
                    size="small"
                    startIcon={<RemoveIcon />}
                    onClick={() => {
                      handleRemoveRecord(index);
                    }}
                  >
                    Remove
                  </AppButton>
                )}
              </>
            )}
          </div>
          <Grid item container md={12} lg={12} spacing={1}>
            {props.fieldsArray?.map((rows:any) => {
              return (
                <Grid item xs={rows.grid.xs} md={rows.grid.md} lg={rows.grid.lg}>
                  {rows.fields.map((fields:any) => {
                    fields['name'] = fields.dynamicName ? `${fields.dynamicName} ${index + 1}` : fields.name;
                    return <RowContent arrayIndex={index} fields={{ ...fields }} arrayName={`${props.metadata?.name}.${index}`} />;
                  })}
                </Grid>
              );
            })}
          </Grid>
          {!props.metadata?.removeMultiple && index === arrayFields.length - 1 && (
            <Box display={'flex'} flexDirection={'row-reverse'} justifyContent={'space-between'}>
              <Box />
              <AppButton
                width="100px"
                background={KenColors.ClientPrimaryGrey}
                variant="contained"
                size="small"
                className={classes.arrayButton}
                startIcon={<AddIcon />}
                onClick={() => {
                  append(props.defaultFields);
                }}
              >
                Add more
              </AppButton>
            </Box>
          )}
        </Box>
      );
  }
};

const ArrayFields: React.FunctionComponent<EventFormJsonFields> = (props) => {
  const { handleSubmit, control } = useFormContext();
  const { fields: arrayFields, append, remove } = useFieldArray({ name: props.metadata?.name as string, control: control });
  const classes = useStyles();

  useEffect(() => {
    append(props.defaultFields);
  }, []);

  const handleRemoveRecord = (key: number) => {
    if (arrayFields.length === 1) {
      return enqueueSnackbar({ message: 'Atlest one record is required', variant: 'error', autoHideDuration: 1000 });
    } else {
      remove(key);
    }
  };

  return arrayFields.map((items: any, index: number) => (
    <ArrayFieldRow
      key={items.id}
      items={items}
      index={index}
      props={props}
      arrayFields={arrayFields}
      handleRemoveRecord={handleRemoveRecord}
      append={append}
      classes={classes}
      control={control}
      handleSubmit={handleSubmit}
    />
  ));
};

export default ArrayFields;
